/**
 * PRP-WEB-1 W2.1: the dashboard's tabs and its Estimates tab.
 *
 * Estimates = every stored estimate the user has (`GET /imports/`, chat and imported), newest
 * first: Name, Project (a link, or "Add to project" for one in no project — PRP-FEEDBACK-2's
 * AddToProjectDialog + `linkImportToProject`), Source (Chat / Imported), Created, Open.
 * The list reloads when the tab comes back into view, so an estimate made in the chat meanwhile
 * shows up.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { BackendAPI } from '../../lib/api';
import { cn, getProjectDisplayName } from '../../lib/utils';
import type { ImportSummary, Project } from '../../types';
import { fmtDate } from '../lib/format';
import AddToProjectDialog from './AddToProjectDialog';
import { CHAT_ESTIMATE_PREFIX, chatEstimateName, estimatePageUrl, importName } from './ChatEstimatesCard';
import { DataTable } from './DataTable';
import { Banner } from './ui/banner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { EmptyState } from './ui/empty-state';
import { Pill } from './ui/pill';
import { IconLayers } from './icons';

/** The dashboard's two views; `?tab=estimates` selects the second. */
export type DashTab = 'projects' | 'estimates';

/** The dashboard tab a `?tab=` value names (Projects unless it says estimates). */
export function dashTabFromParam(v: string | null): DashTab {
  return v === 'estimates' ? 'estimates' : 'projects';
}

/** The dashboard tab from `?tab=`, and a setter that rewrites the URL in place. */
export function useDashTab(): [DashTab, (_t: DashTab) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const select = useCallback(
    (t: DashTab) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (t === 'estimates') next.set('tab', 'estimates');
          else next.delete('tab');
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  return [dashTabFromParam(searchParams.get('tab')), select];
}

/** Projects | Estimates. */
export function DashboardTabs({ tab, onSelect }: { tab: DashTab; onSelect: (_t: DashTab) => void }) {
  const tabs: { id: DashTab; label: string }[] = [
    { id: 'projects', label: 'Projects' },
    { id: 'estimates', label: 'Estimates' },
  ];
  return (
    <div role="tablist" aria-label="Dashboard views" className="mb-3.5 flex gap-1 border-b border-[var(--bd-subtle)]">
      {tabs.map((t) => (
        <button
          key={t.id}
          id={`dash-tab-${t.id}`}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          tabIndex={tab === t.id ? 0 : -1}
          onClick={() => onSelect(t.id)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              e.preventDefault();
              onSelect(t.id === 'projects' ? 'estimates' : 'projects');
            }
          }}
          className={cn(
            '-mb-px border-b-2 px-2 py-2 text-[13px] font-[560] outline-none',
            'focus-visible:rounded-[var(--r-xs)] focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]',
            tab === t.id
              ? 'border-[var(--accent)] text-[var(--tx-hi)]'
              : 'border-transparent text-[var(--tx-lo)] hover:text-[var(--tx-hi)]',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

const isChat = (r: ImportSummary) => (r.parse_hash ?? '').startsWith(CHAT_ESTIMATE_PREFIX);

/** The Estimates tab. `projects` names the Project column's links. */
export default function EstimatesTable({ projects }: { projects: Project[] }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ImportSummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [adding, setAdding] = useState<ImportSummary | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.resolve(BackendAPI.listImports())
      .then((all) => {
        setRows(Array.isArray(all) ? all.filter((r) => r && r.parse_hash) : []);
        setFailed(false);
      })
      .catch(() => {
        setRows((prev) => prev ?? []);
        setFailed(true);
      });
  }, []);

  useEffect(() => {
    load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const byId = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

  const columns = useMemo<ColumnDef<ImportSummary, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (r) => (isChat(r) ? chatEstimateName(r) : importName(r)),
        sortingFn: 'text',
        cell: ({ getValue }) => <span className="font-[530] text-[var(--tx-hi)]">{String(getValue())}</span>,
      },
      {
        id: 'project',
        header: 'Project',
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          if (!r.project_id) {
            return (
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setLinkError(null);
                  setAdding(r);
                }}
                data-testid={`add-to-project-${r.parse_hash}`}
              >
                Add to project
              </Button>
            );
          }
          const p = byId.get(r.project_id);
          return (
            <Link
              to={`/project/${encodeURIComponent(r.project_id)}`}
              onClick={(e) => e.stopPropagation()}
              className="text-[var(--tx-mid)] hover:text-[var(--accent-ink)] hover:underline"
            >
              {p ? getProjectDisplayName(p) : 'Open project'}
            </Link>
          );
        },
      },
      {
        id: 'source',
        header: 'Source',
        accessorFn: (r) => (isChat(r) ? 'Chat' : 'Imported'),
        cell: ({ getValue }) => <Pill kind={getValue() === 'Chat' ? 'accent' : 'neutral'}>{String(getValue())}</Pill>,
      },
      {
        id: 'created',
        header: 'Created',
        accessorFn: (r) => r.created_at ?? '',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className="font-[var(--font-mono)] tabular-nums">{fmtDate((row.original.created_at ?? '').slice(0, 10) || null)}</span>
        ),
      },
      {
        id: 'open',
        header: '',
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <Button
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(estimatePageUrl(row.original));
            }}
            data-testid={`estimate-open-${row.original.parse_hash}`}
          >
            Open
          </Button>
        ),
      },
    ],
    [byId, navigate],
  );

  if (rows === null) {
    return <p className="text-[13px] text-[var(--tx-lo)]" role="status">Loading estimates…</p>;
  }

  return (
    <div data-testid="estimates-tab">
      {failed && (
        <Banner tone="warn" className="mb-3">
          Your estimates could not be loaded.{' '}
          <button type="button" className="underline" onClick={load}>
            Try again
          </button>
        </Banner>
      )}
      {linkError && (
        <Banner tone="warn" className="mb-3">
          {linkError}
        </Banner>
      )}
      {rows.length === 0 && !failed ? (
        <Card>
          <EmptyState icon={<IconLayers />} title="No estimates yet" description="Import a PDF, or build one in chat." />
        </Card>
      ) : (
        <DataTable<ImportSummary>
          columns={columns}
          data={rows}
          initialSorting={[{ id: 'created', desc: true }]}
          onRowClick={(r) => navigate(estimatePageUrl(r))}
        />
      )}
      {adding && (
        <AddToProjectDialog
          header={null}
          onClose={() => setAdding(null)}
          onPick={(p) => {
            const hash = adding.parse_hash;
            setAdding(null);
            void Promise.resolve(BackendAPI.linkImportToProject(hash, p.id))
              .then(() => setRows((prev) => (prev ?? []).map((r) => (r.parse_hash === hash ? { ...r, project_id: p.id } : r))))
              .catch(() => setLinkError('That estimate could not be added to the project. Please try again.'));
          }}
        />
      )}
    </div>
  );
}
